import {
	ChangeDetectionStrategy,
	Component,
	computed,
	OnInit,
	inject,
	input,
} from '@angular/core';
import { NavigationService } from '@services/nav/nav.service';
import { IntakeHeaderViewModel } from '../../view-models/models';
import { TestHooks } from './intake-header.hooks';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { PortalInviteStatus } from '@models/portal-invites';
import { DeleteIntakeFormModalComponent } from '../delete-intake-form-modal/delete-intake-form-modal.component';
import { UI_TYPES } from 'src/app/ui/modal/modal.types';
import { dispatch, select } from '@ngxs/store';
import { DeactivatePortalInvite } from 'src/app/shared/state/portal-invite/portal-invite.actions';
import { DeactivateIntakeForm } from 'src/app/shared/state/matter/client-intake/client-intake.actions';
import { TestHookDirective } from '@core/ui';
import { Logger } from '@core/logger';
import { BusinessUsersState } from 'src/app/shared/state/business-users/business-users.state';
import { MatterClients } from 'src/app/shared/state/global/matter_clients';
import { SelectedMatter } from 'src/app/shared/state/matter/matter.selectors';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { IntakeStatusDisplayComponent } from '../intake-status-display/intake-status-display.component';
import { BusinessFeatureFlagSelectors } from 'src/app/shared/state/business/business-feature-flag.selectors';
import { IntakeFormStatus } from '@models/client-intake';
import { UiModule } from 'src/app/ui/ui.module';
import { KebabOption } from '../../../components/client-intake-actions/view-models/models';
import { ClientIntakeService } from '@services/client-intake/client-intake.service';
import { MatterFileType } from '@models/matter';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';

@Component({
	selector: 'app-intake-header',
	templateUrl: './intake-header.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		ButtonComponent,
		TestHookDirective,
		IntakeStatusDisplayComponent,
		UiModule,
	],
})
export class IntakeHeaderComponent implements OnInit {
	testHooks = TestHooks;
	viewModel_ = input<IntakeHeaderViewModel>();
	portalInviteStatus = PortalInviteStatus;
	clients_ = select(MatterClients.get_matterClientsWithAssociationMapped);
	matterFileType_ = select(SelectedMatter.matterFileType);
	loggedInUser_ = select(BusinessUsersState.getLoggedInBusinessUser);
	useEmailIntegrationForIntake_ = select(
		BusinessFeatureFlagSelectors.isOutlookIntegrationEnabledForClientIntake,
	);

	private modalService = inject(ModalService);
	private navService = inject(NavigationService);
	private clientIntakeService = inject(ClientIntakeService);
	private analyticsService = inject(AnalyticsService);

	private deactivateIntakeForm = dispatch(DeactivateIntakeForm);
	private deactivatePortalInvite = dispatch(DeactivatePortalInvite);

	outlookIntegrationEnabled_ = select(
		BusinessFeatureFlagSelectors.isOutlookIntegrationEnabled,
	);

	nextBtnIcon_ = computed(() => {
		// Currently the Outlook integration flow is primarily triggered when the isOutlookIntegrationEnabled feature flag is turned on
		// In the future, a check will be added to see if the user has connected their Outlook account as well [Issue #1762797942139]
		return this.outlookIntegrationEnabled_() &&
			this.useEmailIntegrationForIntake_()
			? 'chevron-right'
			: 'external-link';
	});

	canUseEmailIntegration_ = computed(() => {
		// Currently the Outlook integration flow is primarily triggered when the isOutlookIntegrationEnabled feature flag is turned on
		// In the future, a check will be added to see if the user has connected their Outlook account as well [Issue #1762797942139]
		return (
			this.outlookIntegrationEnabled_() && this.useEmailIntegrationForIntake_()
		);
	});

	kebabItems = [
		{
			id: KebabOption.ViewResponses,
			title: {
				label: 'View responses',
				icon: 'file-text',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.analyticsService.logEvent(
					AnalyticsEvent.INTAKE_PARTIES_INTAKE_OVERVIEW_VIEW_RESPONSES,
				);

				this.navService.goToMatter_ClientIntake_Review(
					this.viewModel_().matterId,
				);
			},
		},
		{
			id: KebabOption.ViewCustomResponses,
			title: {
				label: 'View custom responses',
				icon: 'north-star',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.navService.goToMatter_ClientIntake_Review_CustomQuestionsTab(
					this.viewModel_().matterId,
				);
			},
		},
		{
			id: KebabOption.Share,
			title: {
				label: 'Share',
				icon: 'mail',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.clientIntakeService.shareIntakeForm();
			},
		},
		{
			id: KebabOption.MarkAsComplete,
			title: {
				label: 'Mark as complete',
				icon: 'check',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.clientIntakeService.setIntakeFormStatus(IntakeFormStatus.COMPLETE);
			},
		},
		{
			id: KebabOption.Cancel,
			title: {
				label: 'Cancel',
				icon: 'circle-x',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.clientIntakeService.setIntakeFormStatus(
					IntakeFormStatus.CANCELLED,
				);
			},
		},
	];

	ngOnInit(): void {
		if (!this.viewModel_()?.portalInvite) {
			Logger.warn(
				'No portal invite found, redirecting to Parties page',
				this.viewModel_(),
			);
			this.navService.goToMatter_Parties(this.viewModel_().matterId);
		}
	}

	sendIntakeEmail() {
		this.analyticsService.logEvent(
			AnalyticsEvent.INTAKE_PARTIES_INTAKE_OVERVIEW_SEND_REQUEST_EMAIL,
		);

		this.clientIntakeService.sendIntakeEmail();
	}

	viewResponses(): void {
		if (!this.viewModel_().matterId) return;

		this.navService.goToMatter_ClientIntake_Review(this.viewModel_().matterId);
	}

	goBack() {
		if (this.matterFileType_() === MatterFileType.REFINANCE) {
			this.navService.goToMatter_Parties_Mortgagors(this.viewModel_().matterId);
			return;
		}

		this.navService.goToMatter_Parties(this.viewModel_().matterId);
	}

	cancelIntake() {
		this.modalService.openModal(DeleteIntakeFormModalComponent, {
			title: 'Delete Request',
			size: 'sm',
			type: UI_TYPES.DANGER,
			primaryAction: {
				text: 'Delete Request',
				action: () => {
					this.modalService.closeModal();
					this.deactivatePortalInvite({
						inviteId: this.viewModel_().portalInvite.id,
						updatedBy: this.viewModel_().loggedInUser.id,
					});
					this.deactivateIntakeForm({
						intakeFormId: this.viewModel_().intakeForm.id,
					});
					this.navService.goToMatter_Parties(this.viewModel_().matterId);
				},
			},
		});
	}

	sendIntakeFollowUpEmail() {
		this.clientIntakeService.sendIntakeEmail();
	}
}
